import * as Sentry from "@sentry/react";
import { useFirebase } from "containers/FirebaseContext";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { filter, find, get, has, map, shuffle } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useUsersByClass } from "store/userClasses/selectors";
import { getClassUsers } from "store/userClasses/thunks";
import { useCurrentUser } from "store/users/selectors";
import { revokeTrafficModal } from "store/wheel/actions";
import { useTraffic } from "store/wheel/selectors";
import {
  updateWheelSelectedUser,
  updateWheelUser,
} from "store/widgetWheel/actions";
import { useWidgetWheel } from "store/widgetWheel/selectors";
import { COLORS } from "utils/config/reference";
import { useUserWidgetsConfig } from "utils/methods/firebaseUserWidgets";
import { generateRandomLightColor } from "utils/methods/math";
import WidgetEditModal from "modules/widget/widgetEditModal";
import WidgetWheelComponent from "./widgetWheelComponent";
import SentryWrap from 'components/Sentry';

const WheelWidget = ({ currentClass }) => {
  const dispatch = useDispatch();
  //states
  const [segColors, setSegColors] = useState([]);
  const [result, setResult] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loader, setLoader] = useState({ type: "", value: null });
  const [fetchAllStudents, setFetchAllStudents] = useState([]);

  // custom hooks
  const wheelWidget = useWidgetWheel();
  const [trafficModal] = useTraffic();
  const usersByClass = useUsersByClass();
  // const currentClass = useCurrentClass();
  const { firestore } = useFirebase();
  const currentUser = useCurrentUser();
  const { createUserWidgetsConfig } = useUserWidgetsConfig();

  //memo declare
  const trafficData = useMemo(
    () => trafficModal?.widget_menu ?? [],
    [trafficModal]
  );
  const filterByWidget = useMemo(() => {
    return find(trafficData, (item) => item?.menu === "widget_wheel");
  }, [trafficData]);

  const isTeacher = useMemo(
    () => get(currentUser, "role") === "teacher",
    [currentUser]
  );

  const open = useMemo(() => {
    return filterByWidget?.view && filterByWidget?.menu === "widget_wheel";
  }, [filterByWidget]);

  const getFetchStudent = async () => {
    try {
      if (!currentClass?.id) return;
      const studentsData = await getClassUsers(currentClass?.id);
      const filterStudent =
        filter(studentsData, (x) => x.role === "student" && x.active &&x?.userclass?.active) || [];
      setFetchAllStudents(filterStudent);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    getFetchStudent();
  }, [currentClass?.id, open]);

  const students = useMemo(
    () => fetchAllStudents || [],
    [fetchAllStudents, currentClass?.id]
  );


  const listenToWidgetUserConfig = async (classID) => {
    if (
      !classID ||
      !currentUser ||
      filterByWidget?.menu !== "widget_wheel" ||
      !open
    ) {
      return () => {};
    }
    try {
      if (loader?.type === "fetchConent" && loader?.value) return;
      setLoader({ type: "fetchContent", value: true });
      const fifteenHoursAgo = new Date();
      fifteenHoursAgo.setHours(fifteenHoursAgo.getHours() - 12);
      const collectionRef = collection(
        firestore,
        `widgets_user_config/${classID}/widgets`
      );
      const q = query(
        collectionRef,
        where("id", "==", "widget_wheel"),
        where("modified_on", ">=", fifteenHoursAgo)
      );

      const unsubscribe = onSnapshot(
        q,
        async (querySnapshot) => {
          setLoader({ type: "fetchContent", value: true });
          let sharableData = null;
          querySnapshot.forEach((doc) => {
            sharableData = { id: doc.id, ...doc.data() };
          });
          if (
            !!sharableData &&
            sharableData?.user_data?.length !== 0 &&
            sharableData?.selectedUser?.length !== 0
          ) {
            const user = await getUsers();
            if (user?.length > 0) {
              const allUserId = user?.map((ele) => ele.id) || [];
              const filteredUser =
                sharableData?.user_data?.filter(
                  (ele) => !allUserId?.includes(ele.id)
                ) || [];
              const payload = [...user, ...filteredUser];
              await dispatch(updateWheelUser(payload || []));
            } else {
              await dispatch(updateWheelUser(sharableData?.user_data || []));
            }
            await dispatch(
              updateWheelSelectedUser(sharableData?.selectedUser || [])
            );
            if (!sharableData?.user_data?.includes(result)) {
              setResult("");
            }
            const segColors = map(
              [...sharableData?.user_data],
              (k, i) => `${COLORS[i] ? COLORS[i] : generateRandomLightColor()}`
            );
            setSegColors(segColors);
          } else {
            const user = await getUsers();
            const segColors = map(
              user,
              (k, i) => `${COLORS[i] ? COLORS[i] : generateRandomLightColor()}`
            );
            setSegColors(segColors);
            await dispatch(updateWheelUser(shuffle(user) || []));
            await dispatch(updateWheelSelectedUser(user || []));
          }
          setLoader({ type: "fetchContent", value: false });
        },
        (error) => {
          setLoader({ type: "fetchContent", value: false });
          console.error(
            "Error listening to widgets user config changes:",
            error
          );
        }
      );

      return unsubscribe;
    } catch (error) {
      Sentry.captureException(
        `399 Error during listenToWidgetUserConfig: ${error}`
      );
      console.log(error);
    }
  };

  useEffect(() => {
    let unsubscribe = () => {};
    listenToWidgetUserConfig(get(currentClass, "id", "")).then(
      (unsubscribeFunc) => {
        unsubscribe = unsubscribeFunc;
      }
    );
    return () => {
      unsubscribe();
    };
  }, [currentClass.id, currentUser, usersByClass[currentClass.id], students]);

  useEffect(() => {
    if (open) {
      setResult("");
      setLoader({ type: "fetchContent", value: true });
    }
  }, [open]);

  useEffect(() => {
    setResult("");
  }, [students]);

  const getUsers = useCallback(() => {
    if (students && has(currentClass, "id")) {
      const user = students?.map((data) => {
        return {
          userName: `${data.lastName || ""} ${data.firstName || ""}`,
          id: data?.id,
          role: data?.role,
        };
      });

      return user || [];
    }
    return [];
  }, [currentClass, students]);

  const onClose = async () => {
    await dispatch(revokeTrafficModal(filterByWidget?.id));
    await dispatch(updateWheelUser([]));
    await dispatch(updateWheelSelectedUser([]));
    setResult("");
    setLoader({ type: "", value: null });
  };

  const handleOpenEditModal = (value) => {
    setOpenEditModal(value);
  };

  const handlerDone = async (users, selectedUser, action) => {
    setLoader({ type: "save-loader", value: true });
    try {
      const WidgetsData = {
        id: "widget_wheel",
        modified_on: new Date(),
        user_data: users,
        selectedUser: selectedUser,
      };
      await createUserWidgetsConfig(WidgetsData, currentClass?.id);
      handleOpenEditModal(false);
    } catch (error) {
      console.log(error);
      Sentry.captureException(`400 Error during handlerDone: ${error}`);
    } finally {
      setLoader({ type: "", value: false });
    }
  };

  const onFinished = async (winner) => {
    if (wheelWidget?.selectedUser?.length > 2) {
      const winnerIndex = wheelWidget.selectedUser.findIndex(
        (user) => user.userName === winner
      );

      if (winnerIndex !== -1) {
        const updatedSelectedUsers = [
          ...wheelWidget.selectedUser.slice(0, winnerIndex),
          ...wheelWidget.selectedUser.slice(winnerIndex + 1),
        ];
        await dispatch(updateWheelSelectedUser(updatedSelectedUsers));
        setSegColors((prevState) =>
          prevState?.filter((_, index) => index !== winnerIndex)
        );
      }
    }

    setResult(winner);
  };

  const resetAllList = async () => {
    setLoader({ type: "reset-list", value: true });
    const user = await getUsers();
    try {
      const WidgetsData = {
        id: "widget_wheel",
        modified_on: new Date(),
        user_data: [],
        selectedUser: [],
      };
      await createUserWidgetsConfig(WidgetsData, currentClass?.id);
      const segColors = map(
        user,
        (k, i) => `${COLORS[i] ? COLORS[i] : generateRandomLightColor()}`
      );
      setSegColors(segColors);
      await dispatch(updateWheelUser(shuffle(user) || []));
      await dispatch(updateWheelSelectedUser(user || []));
      setResult("");
      handleOpenEditModal(false);
    } catch (error) {
      console.log(error);
      Sentry.captureException(`401 Error during resetAllList: ${error}`);
    } finally {
      setLoader({ type: "", value: false });
    }
  };

  return (
    <SentryWrap>
      <div
        style={{
          position: "relative",
          zIndex: 999999999,
        }}
      >
        {filterByWidget?.view && filterByWidget?.menu === "widget_wheel" && (
          <WidgetWheelComponent
            states={{
              result,
              isTeacher,
              selectedUser: wheelWidget?.selectedUser,
              segColors,
              loader: loader?.type === "" && loader?.value === null ? null :  loader?.type === "fetchContent" && loader?.value ,
            }}
            funcObj={{ onClose, handleOpenEditModal, onFinished }}
          />
        )}
      </div>

      <WidgetEditModal
        open={openEditModal}
        close={() => handleOpenEditModal(false)}
        users={wheelWidget?.users}
        selectedUser={wheelWidget?.selectedUser}
        loader={loader}
        handlerDone={handlerDone}
        resetAllList={resetAllList}
      />
    </SentryWrap>
  );
};

export default WheelWidget;
